/**
 * CostSchemeTab — public entry point of the Cost Scheme (Plánovač programu) agenda, embedded in the serie
 * and term detail windows. This is a thin LOADER: it fetches the serie/term program schema and, once
 * available, hands off to {@link CostSchemeContent} which owns the editable board. Loading and error states
 * render here so the content never mounts without data.
 */
import { useTranslation } from 'react-i18next';

import type { SerieLayerStateItem } from '@/api/generated/schemas';
import {
  useGetVenuaPlannerSerieProgramScheme,
  useGetVenuaPlannerTermProgramScheme,
} from '@/api/generated/venua-planner-planning/venua-planner-planning';
import { DETAIL_QUERY_STALE_TIME_MS } from '@/shared/lib/queryClient';
import { Alert } from '@/shared/ui';
import { CostSchemeContent } from './CostSchemeContent';

type CostSchemeTabProps =
  | {
      scope: 'serie';
      productId: string;
      serieId: string;
      termId?: undefined;
      /** Vrstvy seriálu – schvalovací tlačítko v patičce boardu. */
      layers: SerieLayerStateItem[];
      serieLayers?: undefined;
      onBack: () => void;
    }
  | {
      scope: 'term';
      productId: string;
      serieId: string;
      termId: string;
      /** Vrstvy termínu. */
      layers: SerieLayerStateItem[];
      /** Vrstvy seriálu – zdroj nabídky „schválit až po X“. */
      serieLayers: SerieLayerStateItem[];
      onBack: () => void;
    };

export function CostSchemeTab(props: CostSchemeTabProps) {
  const { t } = useTranslation('venua-planner');

  const serieQuery = useGetVenuaPlannerSerieProgramScheme(props.productId, props.serieId, {
    query: { staleTime: DETAIL_QUERY_STALE_TIME_MS, enabled: props.scope === 'serie' },
  });
  const termQuery = useGetVenuaPlannerTermProgramScheme(
    props.productId,
    props.serieId,
    props.scope === 'term' ? props.termId : '',
    { query: { staleTime: DETAIL_QUERY_STALE_TIME_MS, enabled: props.scope === 'term' } }
  );

  const query = props.scope === 'serie' ? serieQuery : termQuery;

  if (query.isLoading) {
    return (
      <div className="bg-background flex h-full items-center justify-center">
        <p className="text-phx-text-muted text-phx-sm">{t('costScheme.loading')}</p>
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <div className="bg-background h-full p-4">
        <Alert severity="error" title={t('costScheme.loadErrorTitle')}>
          {t('costScheme.loadError')}
        </Alert>
      </div>
    );
  }

  return <CostSchemeContent {...props} response={query.data} />;
}
